import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageDetect } from '@/hooks/useLanguageDetect';
import { supabase } from '@/lib/supabase';

interface Category {
  id: string;
  name: string;
  name_zh_tw: string;
  slug: string;
  icon: string;
  description: string;
  image_url: string;
}

export default function CategoryGrid() {
  const { t } = useTranslation();
  const { language } = useLanguageDetect();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const { data, error: supabaseError } = await supabase
          .from('categories')
          .select('id, name, name_zh_tw, slug, icon, description, image_url')
          .order('sort_order', { ascending: true });

        if (supabaseError) throw supabaseError;
        setCategories(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load categories');
      } finally {
        setLoading(false);
      }
    }

    fetchCategories();
  }, []);

  if (error) {
    return (
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <p className="text-foreground-500">{t('loadError')}</p>
          <button
            onClick={() => { setError(null); setLoading(true); window.location.reload(); }}
            className="mt-4 text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer"
          >
            {t('retry') || '重新加载'}
          </button>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-14">
            <div className="w-40 h-4 bg-background-200 rounded-full mx-auto mb-3 animate-pulse" />
            <div className="w-64 h-8 bg-background-200 rounded-full mx-auto mb-3 animate-pulse" />
            <div className="w-80 h-4 bg-background-200 rounded-full mx-auto animate-pulse" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200">
                <div className="w-full h-28 rounded-lg bg-background-200 animate-pulse mb-4" />
                <div className="w-20 h-4 bg-background-200 rounded animate-pulse mb-1.5" />
                <div className="w-full h-3 bg-background-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full py-14 md:py-20 bg-background-100">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xs text-foreground-400 font-medium tracking-widest uppercase mb-3">
            {t('popularNutrients')}
          </p>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
            {t('category')}
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
            探索各类营养素的专业知识，科学了解身体所需
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="group bg-background-50 rounded-xl border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer hover:-translate-y-1 overflow-hidden"
            >
              <div className="w-full h-28 md:h-32 overflow-hidden">
                {cat.image_url ? (
                  <img
                    src={cat.image_url}
                    alt={language === 'zh-Hans' ? cat.name : cat.name_zh_tw}
                    title={language === 'zh-Hans' ? cat.name : cat.name_zh_tw}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-primary-50 text-primary-500 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-2xl`} />
                  </div>
                )}
              </div>
              <div className="p-4 md:p-5">
                <h3 className="font-heading text-sm md:text-base font-bold text-foreground-900 mb-1.5">
                  {language === 'zh-Hans' ? cat.name : cat.name_zh_tw}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                  {cat.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}